/**
 * Audit Report - kPortal Kursauswertung
 * Generates Excel and PDF audit reports from kPortal CSV exports
 */
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft,
  Loader2,
  FileSpreadsheet,
  FileText,
  Upload,
  Calendar,
  Building2,
  Filter,
  CheckCircle,
  AlertCircle,
  Download,
  Users,
  BookOpen,
  Clock,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Color scheme matching specification
const colors = {
  dark: '#0d2440',
  blue: '#2e5e99',
  gray: '#5a7a9b',
  light: '#c8d8e8',
  border: '#e7f0fa',
  bg: '#f5f9fc',
  valid: '#C6EFCE',
  validText: '#006100',
  open: '#FFEB9C',
  openText: '#9C5700',
  expired: '#FFC7CE',
  expiredText: '#9C0006',
};

// Common prefix options
const PREFIX_OPTIONS = [
  { value: 'H&S', label: 'H&S (Health & Safety)' },
  { value: 'Fit4AI', label: 'Fit4AI' },
  { value: 'Fit4Cyber', label: 'Fit4Cyber' },
];

const AuditReport = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // Form state
  const [customerName, setCustomerName] = useState('');
  const [prefix, setPrefix] = useState('H&S');
  const [customPrefix, setCustomPrefix] = useState('');
  const [referenceDate, setReferenceDate] = useState(new Date().toISOString().split('T')[0]);
  const [accountsFile, setAccountsFile] = useState(null);
  const [periodsFile, setPeriodsFile] = useState(null);
  
  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // File input refs
  const accountsInputRef = useRef(null);
  const periodsInputRef = useRef(null);

  const handleAccountsFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().includes('accounts')) {
        toast.warning('Die Datei scheint keine Accounts-Datei zu sein. Bitte prüfen.');
      }
      setAccountsFile(file);
    }
  };

  const handlePeriodsFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().includes('periods')) {
        toast.warning('Die Datei scheint keine Periods-Datei zu sein. Bitte prüfen.');
      }
      setPeriodsFile(file);
    }
  };

  const getEffectivePrefix = () => {
    return prefix === 'custom' ? customPrefix : prefix;
  };

  const validateForm = () => {
    if (!customerName.trim()) {
      toast.error('Bitte Kundennamen eingeben');
      return false;
    }
    if (!getEffectivePrefix().trim()) {
      toast.error('Bitte Kurs-Präfix auswählen oder eingeben');
      return false;
    }
    if (!referenceDate) {
      toast.error('Bitte Stichtag auswählen');
      return false;
    }
    if (!accountsFile) {
      toast.error('Bitte Accounts CSV-Datei hochladen');
      return false;
    }
    if (!periodsFile) {
      toast.error('Bitte Periods CSV-Datei hochladen');
      return false;
    }
    return true;
  };

  const handleGenerateReport = async () => {
    if (!validateForm()) return;
    
    setIsGenerating(true);
    setError(null);
    setResult(null);
    
    try {
      const formData = new FormData();
      formData.append('customer_name', customerName.trim());
      formData.append('prefix', getEffectivePrefix().trim());
      formData.append('reference_date', referenceDate);
      formData.append('accounts_file', accountsFile);
      formData.append('periods_file', periodsFile);
      
      const response = await axios.post(`${API}/api/audit-report/generate`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setResult(response.data);
      toast.success('Report erfolgreich erstellt!');
    } catch (err) {
      console.error('Generate error:', err);
      const message = err.response?.data?.detail || 'Fehler beim Erstellen des Reports';
      setError(message);
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!result?.excel_base64) {
      toast.error('Keine Excel-Daten verfügbar');
      return;
    }
    
    try {
      // Decode base64 to binary
      const binaryString = atob(result.excel_base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.excel_filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Excel-Download gestartet');
    } catch (err) {
      console.error('Excel download error:', err);
      toast.error('Fehler beim Excel-Download');
    }
  };

  const handleDownloadPDF = () => {
    if (!result?.pdf_base64) {
      toast.error('Keine PDF-Daten verfügbar');
      return;
    }
    
    try {
      // Decode base64 to binary
      const binaryString = atob(result.pdf_base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.pdf_filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('PDF-Download gestartet');
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Fehler beim PDF-Download');
    }
  };

  const resetForm = () => {
    setResult(null);
    setError(null);
    setAccountsFile(null);
    setPeriodsFile(null);
    if (accountsInputRef.current) accountsInputRef.current.value = '';
    if (periodsInputRef.current) periodsInputRef.current.value = '';
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/dashboard')} 
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.dark }}>
              Audit Report
            </h1>
            <p className="mt-1" style={{ color: colors.gray }}>
              kPortal Kursauswertung — Excel & PDF Export
            </p>
          </div>
        </div>

        {!result ? (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Form Card */}
            <Card className="bg-white" style={{ borderColor: colors.border }}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: colors.dark }}>
                  <Filter className="h-5 w-5" />
                  Report Konfiguration
                </CardTitle>
                <CardDescription style={{ color: colors.gray }}>
                  Konfigurieren Sie die Parameter für den Audit Report
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Customer Name */}
                <div className="space-y-2">
                  <Label htmlFor="customerName" className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" style={{ color: colors.blue }} />
                    Kundenname *
                  </Label>
                  <Input
                    id="customerName"
                    data-testid="customer-name-input"
                    placeholder="z.B. Ebner"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    style={{ borderColor: colors.border }}
                  />
                  <p className="text-xs" style={{ color: colors.gray }}>
                    Wird im Report-Titel angezeigt
                  </p>
                </div>

                {/* Prefix Filter */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" style={{ color: colors.blue }} />
                    Kurs-Präfix-Filter *
                  </Label>
                  <Select value={prefix} onValueChange={setPrefix}>
                    <SelectTrigger data-testid="prefix-select" style={{ borderColor: colors.border }}>
                      <SelectValue placeholder="Präfix auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {PREFIX_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">Eigener Präfix...</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {prefix === 'custom' && (
                    <Input
                      data-testid="custom-prefix-input"
                      placeholder="Eigenen Präfix eingeben"
                      value={customPrefix}
                      onChange={(e) => setCustomPrefix(e.target.value)}
                      className="mt-2"
                      style={{ borderColor: colors.border }}
                    />
                  )}
                  <p className="text-xs" style={{ color: colors.gray }}>
                    Nur Kurse mit diesem Präfix werden ausgewertet
                  </p>
                </div>

                {/* Reference Date */}
                <div className="space-y-2">
                  <Label htmlFor="referenceDate" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" style={{ color: colors.blue }} />
                    Stichtag *
                  </Label>
                  <Input
                    id="referenceDate"
                    data-testid="reference-date-input"
                    type="date"
                    value={referenceDate}
                    onChange={(e) => setReferenceDate(e.target.value)}
                    style={{ borderColor: colors.border }}
                  />
                  <p className="text-xs" style={{ color: colors.gray }}>
                    Berechnung für "abgelaufen" basiert auf diesem Datum
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* File Upload Card */}
            <Card className="bg-white" style={{ borderColor: colors.border }}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: colors.dark }}>
                  <Upload className="h-5 w-5" />
                  CSV Dateien hochladen
                </CardTitle>
                <CardDescription style={{ color: colors.gray }}>
                  Laden Sie die beiden kPortal Export-Dateien hoch
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Accounts CSV */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="h-4 w-4" style={{ color: colors.blue }} />
                    Accounts CSV *
                  </Label>
                  <div 
                    className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ borderColor: accountsFile ? colors.blue : colors.border }}
                    onClick={() => accountsInputRef.current?.click()}
                  >
                    <input
                      ref={accountsInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleAccountsFileChange}
                      className="hidden"
                      data-testid="accounts-file-input"
                    />
                    {accountsFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span className="font-medium">{accountsFile.name}</span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="h-8 w-8 mx-auto mb-2" style={{ color: colors.gray }} />
                        <p style={{ color: colors.gray }}>emie-report-accounts-*.csv</p>
                        <p className="text-xs mt-1" style={{ color: colors.gray }}>
                          Klicken zum Auswählen
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Periods CSV */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4" style={{ color: colors.blue }} />
                    Periods CSV *
                  </Label>
                  <div 
                    className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ borderColor: periodsFile ? colors.blue : colors.border }}
                    onClick={() => periodsInputRef.current?.click()}
                  >
                    <input
                      ref={periodsInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handlePeriodsFileChange}
                      className="hidden"
                      data-testid="periods-file-input"
                    />
                    {periodsFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span className="font-medium">{periodsFile.name}</span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="h-8 w-8 mx-auto mb-2" style={{ color: colors.gray }} />
                        <p style={{ color: colors.gray }}>emie-report-periods_with_memberships-*.csv</p>
                        <p className="text-xs mt-1" style={{ color: colors.gray }}>
                          Klicken zum Auswählen
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Error Display */}
                {error && (
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Fehler</span>
                    </div>
                    <p className="text-sm text-red-600 mt-1">{error}</p>
                  </div>
                )}

                {/* Generate Button */}
                <Button 
                  className="w-full"
                  style={{ background: colors.blue }}
                  onClick={handleGenerateReport}
                  disabled={isGenerating}
                  data-testid="generate-report-btn"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Report wird erstellt...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Report erstellen
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Results View */
          <div className="space-y-6">
            {/* Success Banner */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-green-100">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-green-800">
                      Report erfolgreich erstellt!
                    </h2>
                    <p className="text-green-700 mt-1">
                      {result.prefix} Audit Report für {result.customer_name} — Stichtag {result.reference_date}
                    </p>
                  </div>
                  <Button variant="outline" onClick={resetForm} data-testid="new-report-btn">
                    Neuer Report
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="bg-white" style={{ borderColor: colors.border }}>
                <CardContent className="p-4 text-center">
                  <Users className="h-6 w-6 mx-auto mb-2" style={{ color: colors.blue }} />
                  <div className="text-2xl font-bold" style={{ color: colors.dark }}>
                    {result.summary?.total_users || 0}
                  </div>
                  <div className="text-sm" style={{ color: colors.gray }}>User</div>
                </CardContent>
              </Card>

              <Card className="bg-white" style={{ borderColor: colors.border }}>
                <CardContent className="p-4 text-center">
                  <BookOpen className="h-6 w-6 mx-auto mb-2" style={{ color: colors.blue }} />
                  <div className="text-2xl font-bold" style={{ color: colors.dark }}>
                    {result.summary?.total_courses || 0}
                  </div>
                  <div className="text-sm" style={{ color: colors.gray }}>Kurse</div>
                </CardContent>
              </Card>

              <Card style={{ background: colors.valid, borderColor: colors.valid }}>
                <CardContent className="p-4 text-center">
                  <CheckCircle className="h-6 w-6 mx-auto mb-2" style={{ color: colors.validText }} />
                  <div className="text-2xl font-bold" style={{ color: colors.validText }}>
                    {result.summary?.valid || 0}
                  </div>
                  <div className="text-sm" style={{ color: colors.validText }}>Gültig</div>
                </CardContent>
              </Card>

              <Card style={{ background: colors.open, borderColor: colors.open }}>
                <CardContent className="p-4 text-center">
                  <Clock className="h-6 w-6 mx-auto mb-2" style={{ color: colors.openText }} />
                  <div className="text-2xl font-bold" style={{ color: colors.openText }}>
                    {result.summary?.open || 0}
                  </div>
                  <div className="text-sm" style={{ color: colors.openText }}>Offen</div>
                </CardContent>
              </Card>

              <Card style={{ background: colors.expired, borderColor: colors.expired }}>
                <CardContent className="p-4 text-center">
                  <XCircle className="h-6 w-6 mx-auto mb-2" style={{ color: colors.expiredText }} />
                  <div className="text-2xl font-bold" style={{ color: colors.expiredText }}>
                    {result.summary?.expired || 0}
                  </div>
                  <div className="text-sm" style={{ color: colors.expiredText }}>Abgelaufen</div>
                </CardContent>
              </Card>
            </div>

            {/* Download Buttons */}
            <Card className="bg-white" style={{ borderColor: colors.border }}>
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: colors.dark }}>
                  Downloads
                </CardTitle>
                <CardDescription style={{ color: colors.gray }}>
                  Laden Sie die generierten Reports herunter
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <Button
                    variant="outline"
                    className="h-24 flex flex-col items-center justify-center gap-2 hover:bg-green-50 hover:border-green-300"
                    onClick={handleDownloadExcel}
                    data-testid="download-excel-btn"
                  >
                    <FileSpreadsheet className="h-8 w-8 text-green-600" />
                    <div>
                      <div className="font-medium">Excel herunterladen</div>
                      <div className="text-xs text-gray-500">{result.excel_filename}</div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-24 flex flex-col items-center justify-center gap-2 hover:bg-red-50 hover:border-red-300"
                    onClick={handleDownloadPDF}
                    data-testid="download-pdf-btn"
                  >
                    <FileText className="h-8 w-8 text-red-600" />
                    <div>
                      <div className="font-medium">PDF herunterladen</div>
                      <div className="text-xs text-gray-500">{result.pdf_filename}</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Info Box */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Report-Inhalte:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Excel:</strong> 3 Sheets — Übersicht, Matrix (User × Kurs), Detail (alle Einträge)</li>
                      <li><strong>PDF:</strong> Professionelles Layout im A4 Querformat mit Farbcodierung</li>
                      <li><strong>Logik:</strong> Pro User/Kurs wird der jüngste abgeschlossene Versuch bevorzugt</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AuditReport;
